import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  Checkbox,
  CheckboxGroup,
  Select,
  SelectItem,
  Tag,
  TextInput,
  Tile,
} from "@carbon/react";
import DataTable, {
  type DataTableColumn,
} from "../../components/carbon/DataTable/DataTable";
import {
  IMPORT_TARGET_FIELDS,
  type ColumnMappingDTO,
  type ImportMappingDTO,
  type ImportPreview,
  type ImportTargetField,
} from "../../types";

type Props = {
  mapping: ImportMappingDTO;
  preview: ImportPreview | null;
  disabled: boolean;
  onChange: (mapping: ImportMappingDTO) => void;
};

/** True when a proposed target is one the platform can actually fill. */
const isKnownField = (target: string) =>
  (IMPORT_TARGET_FIELDS as string[]).includes(target);

/** How many rows a field ended up with. Per target, which is how the backend counts. */
const resultFor = (preview: ImportPreview | null, target: ImportTargetField) =>
  preview?.fields.find((field) => field.target === target) ?? null;

/**
 * A default value, committed when the buyer stops typing rather than on every keystroke.
 *
 * Each commit re-reads the whole file on the server and disables this input while it does, so
 * writing per keystroke meant typing "100" and saving "1".
 */
const DefaultValueCell = ({
  id,
  label,
  value,
  disabled,
  onCommit,
}: {
  id: string;
  label: string;
  value: string;
  disabled: boolean;
  onCommit: (next: string | null) => void;
}) => {
  const [draft, setDraft] = useState(value);

  // Adopt what came back from the server, but not while the buyer is mid-word.
  useEffect(() => {
    setDraft(value);
  }, [value]);

  const commit = () => {
    if (draft !== value) {
      onCommit(draft || null);
    }
  };

  return (
    <TextInput
      id={id}
      labelText={label}
      hideLabel
      size="sm"
      className="pr-import__default-input"
      value={draft}
      placeholder="-"
      onChange={(event) => setDraft(event.target.value)}
      onBlur={commit}
      onKeyDown={(event) => {
        if (event.key === "Enter") (event.target as HTMLInputElement).blur();
      }}
      disabled={disabled}
    />
  );
};

/** The checkboxes that pick columns for one of the two keys: grouping, or line identity. */
const KeyColumnsPicker = ({
  name,
  legend,
  options,
  selected,
  disabled,
  onToggle,
}: {
  name: string;
  legend: string;
  options: string[];
  selected: string[];
  disabled: boolean;
  onToggle: (source: string, checked: boolean) => void;
}) => (
  <CheckboxGroup legendText={legend}>
    {options.map((source) => (
      <Checkbox
        key={`${name}-${source}`}
        id={`pr-import-${name}-${source}`}
        labelText={source}
        disabled={disabled}
        checked={selected.includes(source)}
        onChange={(_event, { checked }) => onToggle(source, checked)}
      />
    ))}
  </CheckboxGroup>
);

type MappingRow = {
  id: string;
  column: React.ReactNode;
  mapsTo: React.ReactNode;
  transforms: React.ReactNode;
  default: React.ReactNode;
  result: React.ReactNode;
};

/**
 * One row per column of the file, and what we decided it means.
 *
 * The point of this screen is that the mapping is the buyer's, not the model's. Every change
 * re-reads their file and the counts beside each column move, so they can see the effect of what
 * they just said rather than being asked to trust it.
 */
const MappingStep = ({ mapping, preview, disabled, onChange }: Props) => {
  const { t } = useTranslation();
  const columns = useMemo(() => mapping.columns ?? [], [mapping.columns]);
  const groupBy = mapping.groupBy ?? [];
  const identityKey = mapping.identityKey ?? [];

  const groupingOptions = useMemo(
    () => columns.map((column) => column.source),
    [columns]
  );

  const changeColumn = (index: number, patch: Partial<ColumnMappingDTO>) => {
    const next = columns.map((column, i) =>
      i === index ? { ...column, ...patch } : column
    );
    onChange({ ...mapping, columns: next });
  };

  const toggleKeyColumn = (
    key: "groupBy" | "identityKey",
    source: string,
    checked: boolean
  ) => {
    const current = key === "groupBy" ? groupBy : identityKey;
    const next = checked
      ? [...current, source]
      : current.filter((value) => value !== source);
    onChange({ ...mapping, [key]: next });
  };

  /** What a column ended up producing, in one tag. */
  const resultOf = (column: ColumnMappingDTO) => {
    if (column.append) {
      // Counts are per field, not per column, so an appended column would show the same total as
      // the one it appends to. Say what it does instead.
      return (
        <span className="pr-import__muted">
          {t("purchaseRequests.import.appendsTo")}
        </span>
      );
    }
    if (!isKnownField(column.target)) {
      return (
        <Tag type="red" size="sm">
          {t("purchaseRequests.import.goesNowhere")}
        </Tag>
      );
    }
    const stats = resultFor(preview, column.target);
    if (!stats) return <span className="pr-import__muted">-</span>;
    return (
      <>
        <Tag type="green" size="sm">
          {t("purchaseRequests.import.mappedCount", { count: stats.mapped })}
        </Tag>
        {stats.invalid > 0 && (
          <Tag type="red" size="sm">
            {`${stats.invalid} ${t("purchaseRequests.import.invalid")}`}
          </Tag>
        )}
      </>
    );
  };

  const headers: DataTableColumn<MappingRow>[] = (
    ["column", "mapsTo", "transforms", "default", "result"] as const
  ).map((key) => ({ key, header: t(`purchaseRequests.import.table.${key}`) }));

  const rows: MappingRow[] = columns.map((column, index) => ({
    id: column.source,
    column: (
      <span className="pr-import__column-source">
        {column.source}
        {column.rationale && (
          <span
            className="pr-import__column-rationale"
            title={column.rationale}
          >
            {column.rationale}
          </span>
        )}
      </span>
    ),
    mapsTo: (
      <Select
        id={`pr-import-target-${index}`}
        labelText={t("purchaseRequests.import.mapsToLabel", {
          column: column.source,
        })}
        hideLabel
        size="sm"
        className="pr-import__target-select"
        disabled={disabled}
        invalid={!isKnownField(column.target)}
        value={column.target}
        onChange={(event) =>
          changeColumn(index, {
            target: event.target.value as ImportTargetField,
          })
        }
      >
        {/* A field we do not have is shown as itself, not quietly replaced by the first option
            in the list. The model proposed `request.assignedBuyer` once; without this the row
            read as "Item description" and the buyer had no way to know the column was going
            nowhere. */}
        {!isKnownField(column.target) && (
          <SelectItem
            value={column.target}
            text={t("purchaseRequests.import.notAField", {
              field: column.target,
            })}
          />
        )}
        {IMPORT_TARGET_FIELDS.map((field) => (
          <SelectItem
            key={field}
            value={field}
            text={t(`purchaseRequests.import.fields.${field}`)}
          />
        ))}
      </Select>
    ),
    transforms: (
      <>
        {(column.transforms ?? []).map((transform) => (
          <Tag key={transform} type="cool-gray" size="sm">
            {transform}
          </Tag>
        ))}
      </>
    ),
    default: (
      <DefaultValueCell
        id={`pr-import-default-${index}`}
        label={t("purchaseRequests.import.defaultValueLabel", {
          column: column.source,
        })}
        value={column.defaultValue ?? ""}
        disabled={disabled}
        onCommit={(next) => changeColumn(index, { defaultValue: next })}
      />
    ),
    result: resultOf(column),
  }));

  return (
    <>
      <Tile className="pr-import__panel">
        <div className="pr-import__panel-head">
          <h3 className="pr-import__title">
            {t("purchaseRequests.import.grouping")}
          </h3>
          <p className="pr-import__hint">
            {t("purchaseRequests.import.groupingHint")}
          </p>
        </div>

        <div className="pr-import__key-columns">
          <div>
            <KeyColumnsPicker
              name="group"
              legend={t("purchaseRequests.import.groupBy")}
              options={groupingOptions}
              selected={groupBy}
              disabled={disabled}
              onToggle={(source, checked) =>
                toggleKeyColumn("groupBy", source, checked)
              }
            />
            {preview && (
              <p className="pr-import__hint">
                {t("purchaseRequests.import.wouldCreate", {
                  groups: preview.groupCount,
                  items: preview.itemCount,
                })}
              </p>
            )}
          </div>

          <div>
            <KeyColumnsPicker
              name="identity"
              legend={t("purchaseRequests.import.identityKey")}
              options={groupingOptions}
              selected={identityKey}
              disabled={disabled}
              onToggle={(source, checked) =>
                toggleKeyColumn("identityKey", source, checked)
              }
            />
            <p className="pr-import__hint">
              {t("purchaseRequests.import.identityKeyHint")}
            </p>
          </div>
        </div>
      </Tile>

      <Tile className="pr-import__panel pr-import__panel--flush">
        <div className="pr-import__panel-head">
          <h3 className="pr-import__title">
            {t("purchaseRequests.import.columnMapping")}
          </h3>
          <p className="pr-import__hint">
            {t("purchaseRequests.import.columnMappingHint")}
          </p>
        </div>

        <DataTable
          headers={headers}
          rows={rows}
          isLoading={false}
          empty={
            <p className="pr-import__hint">
              {t("purchaseRequests.import.noMapping")}
            </p>
          }
        />
      </Tile>
    </>
  );
};

export default MappingStep;
